/**
 * 하단 시트 (DESIGN_SPEC 6장: 근거 시트). 위쪽 반경 20, 배경 딤, 240ms 로 올라옵니다.
 * 그림자는 이 시트만 아주 옅게 씁니다.
 *
 * 열고 닫는 것은 부르는 쪽이 정합니다. 근거 시트는 라우트(/chat/:memberKey/evidence/:key)
 * 이므로 onClose 가 뒤로 가기입니다. 그래서 안드로이드 뒤로 버튼도 시트만 닫습니다.
 *
 * 열려 있는 동안 뒤 화면의 스크롤을 막고, Esc 로 닫히고, 첫 포커스를 시트 안에 둡니다.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function BottomSheet({ open, onClose, label, children }) {
    const panelRef = useRef(null);
    const dragStart = useRef(null);
    const [dragOffset, setDragOffset] = useState(null);
    // 부르는 쪽이 매번 새 함수를 넘겨도 열 때의 처리(포커스, 스크롤 잠금)를 다시 하지 않습니다.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const finishDrag = (event) => {
        if (dragStart.current === null) {
            return;
        }
        const distance = Math.max(0, event.clientY - dragStart.current);
        dragStart.current = null;
        if (distance > 80) {
            onCloseRef.current();
        } else {
            setDragOffset(0);
        }
    };

    useEffect(() => {
        if (!open) {
            setDragOffset(null);
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const previouslyFocused = document.activeElement;
        panelRef.current?.focus();

        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        }
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
            if (previouslyFocused instanceof HTMLElement) {
                previouslyFocused.focus();
            }
        };
    }, [open]);

    if (!open) {
        return null;
    }

    return createPortal(
        <div className="fixed inset-0 z-50 mx-auto flex max-w-app flex-col bg-dim motion-safe:animate-dim-in">
            <button type="button" aria-label={`${label} 닫기`} onClick={onClose} className="flex-1" tabIndex={-1} />
            <section
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                tabIndex={-1}
                className={`flex max-h-[85dvh] flex-col gap-3 overflow-y-auto rounded-t-sheet bg-surface px-5 pt-3 pb-[max(28px,env(safe-area-inset-bottom))] outline-none ${dragOffset === null ? 'motion-safe:animate-sheet-up' : ''}`}
                style={dragOffset === null ? undefined : {
                    transform: `translateY(${dragOffset}px)`,
                    transition: dragStart.current === null ? 'transform 180ms ease-out' : 'none',
                }}
            >
                <div
                    aria-hidden="true"
                    className="flex h-5 shrink-0 cursor-grab items-start justify-center self-stretch touch-none active:cursor-grabbing"
                    onPointerDown={(event) => {
                        dragStart.current = event.clientY;
                        setDragOffset(0);
                        event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={(event) => {
                        if (dragStart.current !== null) {
                            setDragOffset(Math.max(0, event.clientY - dragStart.current));
                        }
                    }}
                    onPointerUp={finishDrag}
                    onPointerCancel={() => {
                        dragStart.current = null;
                        setDragOffset(0);
                    }}
                >
                    <span className="h-1 w-10 rounded-full bg-border" />
                </div>
                {children}
            </section>
        </div>,
        document.body
    );
}
